import { GraphQLQuery, GraphQLResult } from '@aws-amplify/api';
import { API, graphqlOperation } from 'aws-amplify';
import {
  CreateProcessModuleInput,
  Field,
  FormSubmission,
  FormSubmissionsByProcessModuleQuery,
  GetProcessModuleQuery,
  ListFieldsQuery,
  ListFormsQuery,
  ListProcessModulesQuery,
  ProcessModule,
  TeamModuleConfigurationInput,
} from 'src/API';
import { ProcessModuleCanvas } from 'src/ProcessModuleCanvas';
import {
  formSubmissionsByProcessModule,
  getProcessModule,
  listFields,
  listForms,
  listProcessModules,
} from 'src/graphql/queries';
import { DynamicTableData } from 'src/pages/modules/process-module/FormView';
import { ActivityEvent } from 'src/types/activity-event';
import { UploadFile } from 'src/types/UploadFile';
import PlanvasBaseApi from '../api';
import { ApiResponse } from '../team/ApiResponse';

export interface TeamModules {
  [key: string]: {
    steps: string[];
  };
}

export interface DeleteFileInput {
  teamId: string;
  key: string;
  formSubmissionId: string;
  fieldId: string;
}

export interface FormFieldValueBase {
  value: any;
}
export interface FileUploadSettings extends FormFieldValueBase {
  label: string;
}

export interface CommentSettings extends FormFieldValueBase {
  label: string;
}

export interface LinkSettings extends FormFieldValueBase {
  label: string;
}

export type FormFieldValue =
  | File
  | null
  | undefined
  | string
  | number
  | UploadFile[]
  | DynamicTableData[]
  | { [key: string]: boolean }
  | ActivityEvent[];

export interface FormField {
  fieldID: string;
  fieldName: string | null | undefined;
  value: string | number | boolean | string[];
}

export interface createFormSubmission {
  processModuleID: string;
  formId: string;
  formFields: FormField[];
}

export interface FileUploadInput {
  formId: string;
  fieldId: string;
  formData: FormData;
  processModuleID: string;
}

export interface ProcessModuleOverviewResponse {
  processModuleID: string;
  processModuleName: string;
  teamForms: { [key: string]: TeamForm[] };
}

export interface TeamForm {
  formId: string;
  formName: string;
  status: string;
  sequence: number;
}

// Field type-specific property interfaces
export interface BaseFieldProperties {
  label?: string;
  placeholder?: string;
  required?: boolean;
  visible?: boolean;
  disabled?: boolean;
  access?: 'editor' | 'manager';
  override?: {
    label?: string;
    placeholder?: string;
    style?: Record<string, any>;
  };
}

export interface ActivityTableProperties extends BaseFieldProperties {
  type: typeof FIELD_TYPES.ACTIVITY_TABLE;
  tableConfig?: string;
}

export interface CheckboxListProperties extends BaseFieldProperties {
  type: typeof FIELD_TYPES.CHECKBOX_LIST;
  options: Array<{ name: string; label: string }>;
}

export interface ImageProperties extends BaseFieldProperties {
  type: typeof FIELD_TYPES.IMAGE;
  src: string;
}

export interface InnovationReadinessProperties extends BaseFieldProperties {
  type: typeof FIELD_TYPES.INNOVATION_READINESS_LEVELS;
  levelsConfig?: string;
}

export interface StaticFileProperties extends BaseFieldProperties {
  type: typeof FIELD_TYPES.STATIC_FILE;
  fileUrl?: string;
}

export interface DynamicTableProperties extends BaseFieldProperties {
  type: typeof FIELD_TYPES.DYNAMIC_TABLE;
  tableConfig?: string;
  disableAddColumn?: boolean;
  disableAddRow?: boolean;
  disableEdit?: boolean;
}

// export interface ActionProperties extends BaseFieldProperties {
//   type: typeof FIELD_TYPES.INFINITE_CANVAS;
// }

export interface DropdownProperties extends BaseFieldProperties {
  type: typeof FIELD_TYPES.DROPDOWN;
  options: Array<{ name: string }>;
}

export interface FileUploadProperties extends BaseFieldProperties {
  type: typeof FIELD_TYPES.FILE_UPLOAD;
  acceptedTypes?: string;
}

export interface HtmlProperties extends BaseFieldProperties {
  type: typeof FIELD_TYPES.HTML;
  content: string;
}

export interface CommentProperties extends BaseFieldProperties {
  type: typeof FIELD_TYPES.COMMENT;
  rows?: number;
}

export interface LinkProperties extends BaseFieldProperties {
  type: typeof FIELD_TYPES.LINK;
}

export interface MarkdownProperties extends BaseFieldProperties {
  type: typeof FIELD_TYPES.MARKDOWN;
  content: string;
}

export interface ProcessModuleInfiniteCanvasProperties extends BaseFieldProperties {
  type: typeof FIELD_TYPES.PROCESS_MODULE_INFINITE_CANVAS;
  canvasId: string;
  templateId?: string;
  canvasCreated?: boolean;
}
export interface ProcessModuleRootCanvasProperties extends BaseFieldProperties {
  type: typeof FIELD_TYPES.PROCESS_MODULE_ROOT_CANVAS;
  canvasId?: string;
  templateId?: string;
  canvasCreated?: boolean;
  isRootField?: boolean;
  referencedFieldId?: string;
  override?: {
    label?: string;
  };
}

export type FieldProperties =
  | ActivityTableProperties
  | CheckboxListProperties
  | ImageProperties
  | InnovationReadinessProperties
  | StaticFileProperties
  | DynamicTableProperties
  // | ActionProperties
  | DropdownProperties
  | FileUploadProperties
  | HtmlProperties
  | CommentProperties
  | LinkProperties
  | MarkdownProperties
  | ProcessModuleInfiniteCanvasProperties
  | ProcessModuleRootCanvasProperties;

// Update FieldInput to include properties
export interface FieldInput {
  id: string;
  type: string; //typeof FIELD_TYPES;
  name?: string | null | undefined;
  label: string;
  required?: boolean;
  properties?: FieldProperties & { [key: string]: any };
  moduleId?: string;
  placeholder?: string;
}

export interface FormInput {
  name: string;
  description: string;
  fields: (FieldInput & Partial<Field>)[];
}

export interface FormApiInput {
  name: string;
  description: string;
  fields: any[];
}

export interface FormResponse {
  ok: boolean;
  result: {
    form: {
      id: string;
      name: string;
      description: string;
      enabled: boolean;
      processModuleID: string;
      sequence: number;
      status: string;
      organizationId: string;
      fields: {
        id: string;
        properties: {
          type: string;
          name: string;
          label: string;
          required: boolean;
          options?: string[];
        };
      }[];
    };
    fields: {
      id: string;
      properties: {
        type: string;
        name: string;
        label: string;
        required: boolean;
        options?: string[];
      };
    }[];
  };
  errors?: string[];
}

export interface FieldResponse {
  ok: boolean;
  result: {
    field: {
      id: string;
      type: string;
      name: string;
      label: string;
      required: boolean;
      dataSource?: string;
      dataSourceType?: string;
      organizationId: string;
      properties: {
        [key: string]: any;
      };
    };
  };
}

export const FIELD_TYPES = {
  ACTIVITY_TABLE: 'activity-table',
  CHECKBOX_LIST: 'checkbox-list',
  IMAGE: 'image',
  VIDEO: 'video',
  INNOVATION_READINESS_LEVELS: 'innovation-readiness-levels',
  STATIC_FILE: 'static-file',
  DYNAMIC_TABLE: 'dynamic-table',
  // INFINITE_CANVAS: 'infinite-canvas',
  DROPDOWN: 'dropdown',
  FILE_UPLOAD: 'file-upload',
  HTML: 'html',
  COMMENT: 'comment',
  LINK: 'link',
  MARKDOWN: 'markdown',
  DIVIDER: 'divider',
  PROCESS_MODULE_INFINITE_CANVAS: 'process-module-infinite-canvas',
  PROCESS_MODULE_ROOT_CANVAS: 'process-module-root-canvas',
  // STATUS: 'status',
} as const;

// Custom display names for field types (centralized for maintainability)
export const FIELD_TYPE_DISPLAY_NAMES: { [key: string]: string } = {
  ACTIVITY_TABLE: 'Activity Table',
  CHECKBOX_LIST: 'Checkbox List',
  IMAGE: 'Image',
  VIDEO: 'Video',
  INNOVATION_READINESS_LEVELS: 'Innovation Readiness Levels',
  STATIC_FILE: 'Static File',
  DYNAMIC_TABLE: 'Dynamic Table',
  DROPDOWN: 'Dropdown',
  FILE_UPLOAD: 'File Upload',
  HTML: 'HTML',
  COMMENT: 'Comment',
  LINK: 'Link',
  MARKDOWN: 'Markdown',
  DIVIDER: 'Divider',
  PROCESS_MODULE_INFINITE_CANVAS: 'Infinite Canvas (Deprecated)',
  PROCESS_MODULE_ROOT_CANVAS: 'Canvas',
};

// Helper function to get field type display name
export const getFieldTypeDisplayName = (fieldTypeKey: string): string => {
  if (FIELD_TYPE_DISPLAY_NAMES[fieldTypeKey]) {
    return FIELD_TYPE_DISPLAY_NAMES[fieldTypeKey];
  }
  // Fallback to formatted key name
  return fieldTypeKey
    .split('_')
    .map((word) => word.charAt(0) + word.slice(1).toLowerCase())
    .join(' ');
};

// Helper function to get display name from field type value
export const getFieldTypeDisplayNameFromValue = (fieldTypeValue: string): string => {
  const fieldTypeKey = Object.keys(FIELD_TYPES).find(
    (key) => FIELD_TYPES[key as keyof typeof FIELD_TYPES] === fieldTypeValue,
  );
  return fieldTypeKey ? getFieldTypeDisplayName(fieldTypeKey) : fieldTypeValue;
};

export interface UpdateFieldInput {
  name?: string;
  label?: string;
  type?: string;
  dataSource?: string | null;
  dataSourceType?: string | null;
}

export interface ProcessModuleWithCanvas extends ProcessModule {
  //   infiniteCanvases?: ProcessModuleCanvas[] | null | undefined;
}

class ProcessModuleApi extends PlanvasBaseApi {
  endpoint = '/processmodule';

  moduleConfigurationsPath = (teamId: string): string => `/team/moduleConfigurations/${teamId}`;
  createFormSubmissionPath = (teamId: string): string => `/team/${teamId}/formSubmission`;
  uploadFile = (teamId: string): string => `/team/${teamId}/file`;

  async getOverView(): Promise<{
    errors?: string[];
    result: ProcessModuleOverviewResponse[];
    ok: boolean;
  }> {
    return super.get(`${this.endpoint}/overview`);
  }

  async getProcessModules(): Promise<ProcessModule[] | undefined> {
    try {
      const response = await API.graphql<GraphQLQuery<ListProcessModulesQuery>>(graphqlOperation(listProcessModules));
      return response.data?.listProcessModules?.items as ProcessModule[];
    } catch (error) {
      this.onFailed(error);
      return undefined;
    }
  }

  async updateModuleConfigurationForTeam(
    teamId: string,
    moduleConfigurations: TeamModuleConfigurationInput[],
  ): Promise<ApiResponse<void>> {
    return super.put(this.moduleConfigurationsPath(teamId), {
      moduleConfigurations,
    });
  }

  async getFormsForProcessModule(): Promise<GraphQLResult<GraphQLQuery<ListFormsQuery>>> {
    return API.graphql<GraphQLQuery<ListFormsQuery>>(graphqlOperation(listForms));
  }

  async getFormSubmissionByProcessModuleId(
    processModuleID: string,
  ): Promise<GraphQLResult<GraphQLQuery<FormSubmissionsByProcessModuleQuery>>> {
    return API.graphql<GraphQLQuery<FormSubmissionsByProcessModuleQuery>>(
      graphqlOperation(formSubmissionsByProcessModule, {
        processModuleID: processModuleID,
      }),
    );
  }

  async getFields(): Promise<GraphQLResult<GraphQLQuery<ListFieldsQuery>>> {
    // Use the reusable pagination iterator from base class
    return this.iterateGraphQLQuery<ListFieldsQuery>(listFields as string, 'listFields', 100);
  }

  async createFormSubmission(
    teamId: string,
    input: { processModuleID: string; formId: string; formFields: FormField[] },
  ): Promise<ApiResponse<FormSubmission>> {
    return super.post(this.createFormSubmissionPath(teamId), input);
  }

  async createFile(
    teamId: string,
    input: FileUploadInput,
    onProgress: (progressEvent: ProgressEvent) => void,
  ): Promise<ApiResponse<any>> {
    const { formId, fieldId, formData, processModuleID } = input;
    return super.postWithProgress(
      this.uploadFile(teamId),
      formData,
      { 'Content-Type': 'multipart/form-data' },
      onProgress,
      { params: { formId, fieldId, processModuleID } },
    );
  }

  async deleteFile(input: DeleteFileInput): Promise<ApiResponse<void>> {
    return super.delete(`/team/${input.teamId}/file`, {
      key: input.key,
      formSubmissionId: input.formSubmissionId,
      fieldId: input.fieldId,
    });
  }

  async getPresignedUrl(teamId: string, key: string): Promise<ApiResponse<string>> {
    return super.get(`/team/file/presignedUrl`, { key, teamId });
  }

  async createProcessModule(
    input: CreateProcessModuleInput,
    organizationId: string,
  ): Promise<ApiResponse<ProcessModule>> {
    return super.post(`/processmodule/${organizationId}`, input);
  }

  async updateProcessModule(
    input: Partial<CreateProcessModuleInput>,
    organizationId: string,
    moduleId: string,
  ): Promise<ApiResponse<ProcessModule>> {
    return super.patch(`/processmodule/${organizationId}/${moduleId}`, input);
  }

  async getProcessModule(processModuleID: string): Promise<ProcessModule | undefined> {
    try {
      const response = await API.graphql<GraphQLQuery<GetProcessModuleQuery>>(
        graphqlOperation(getProcessModule, { id: processModuleID }),
      );
      return response.data?.getProcessModule as ProcessModule;
    } catch (error) {
      this.onFailed(error);
      return undefined;
    }
  }

  async createForm(organizationId: string, processModuleID: string, input: FormInput): Promise<FormResponse> {
    console.log('createForm input', input);
    const apiInput: FormApiInput = {
      ...input,
      fields: input.fields.map((field) => {
        const mappedField = {
          id: crypto.randomUUID(),
          type: field.type,
          properties: {
            name: field.name,
            label: field.label,
            required: field.required,
            dataSource: field.dataSource,
            // Preserve all field-specific properties
            // but exclude dataSource to avoid overwriting the field-level dataSource
            ...Object.fromEntries(Object.entries(field.properties || {}).filter(([key]) => key !== 'dataSource')),
          },
        };
        if (field.type === 'process-module-infinite-canvas') {
          console.log('createForm - Canvas field mapping:', {
            originalField: field,
            originalProperties: field.properties,
            mappedField: mappedField,
            canvasId: field.properties?.canvasId,
            mappedCanvasId: (mappedField.properties as any)?.canvasId,
          });
        }
        return mappedField;
      }),
    };
    console.log('createForm apiInput being sent:', JSON.stringify(apiInput, null, 2));
    const response = await super.post(`/processmodule/${organizationId}/${processModuleID}/form`, apiInput);
    return response as FormResponse;
  }

  async updateForm(
    organizationId: string,
    processModuleID: string,
    formId: string,
    input: Partial<FormInput>,
  ): Promise<FormResponse> {
    console.log('updateForm input', input);
    // Remove id/formId from the payload if present at the top level
    const { id, ...rest } = input as any;
    const apiInput: Partial<FormApiInput> = {
      ...rest,
      fields: input.fields
        ? (input.fields.map((field) => {
            // Ensure each field includes its id property
            const { formId, ...fieldRest } = field as any;
            const mappedField = {
              id: field.id, // always include id for backend
              ...fieldRest,
              type: field.type,
              properties: {
                name: field.name,
                label: field.label,
                required: field.required,
                dataSource: field.dataSource,
                placeholder: field.properties?.placeholder,
                template: field.properties?.template,
                access: field.properties?.access,
                // Preserve all field-specific properties including canvas properties
                // but exclude dataSource to avoid overwriting the field-level dataSource
                ...Object.fromEntries(Object.entries(field.properties || {}).filter(([key]) => key !== 'dataSource')),
              },
            };
            if (field.type === 'process-module-infinite-canvas') {
              console.log('updateForm - Canvas field mapping:', {
                originalField: field,
                originalProperties: field.properties,
                mappedField: mappedField,
                canvasId: field.properties?.canvasId,
                mappedCanvasId: mappedField.properties.canvasId,
              });
            }
            return mappedField;
          }) as any)
        : undefined,
    };
    console.log('updateForm apiInput being sent:', JSON.stringify(apiInput, null, 2));
    const response = await super.put(`/processmodule/${organizationId}/${processModuleID}/form/${formId}`, apiInput);
    return response as FormResponse;
  }

  async patchForm(
    organizationId: string,
    processModuleID: string,
    formId: string,
    input: { name?: string; sequence?: number; description?: string },
  ): Promise<FormResponse> {
    const response = await super.patch(`/processmodule/${organizationId}/${processModuleID}/form/${formId}`, input);
    return response as FormResponse;
  }

  async updateFormSequence(
    formId: string,
    sequence: number,
    organizationId: string,
    processModuleID: string,
  ): Promise<ApiResponse<void>> {
    return super.patch(`/processmodule/${organizationId}/${processModuleID}/form/${formId}`, { sequence });
  }

  async deleteForm(
    formId: string,
    organizationId: string,
    processModuleID: string,
    keepFields: boolean,
  ): Promise<ApiResponse<void>> {
    return super.delete(`/processmodule/${organizationId}/${processModuleID}/form/${formId}?keepFields=${keepFields}`);
  }

  // async updateField(organizationId: string, fieldId: string, input: UpdateFieldInput) {
  //     const response = await fetch(
  //         `/api/processmodule/${organizationId}/field/${fieldId}`,
  //         {
  //             method: 'PUT',
  //             headers: {
  //                 'Content-Type': 'application/json',
  //             },
  //             body: JSON.stringify(input),
  //         }
  //     );

  //     if (!response.ok) {
  //         throw new Error('Failed to update field');
  //     }

  //     return response.json();
  // }

  async createField(
    organizationId: string,
    processModuleId: string,
    field: Partial<FieldInput & Partial<Field> & { formId: string }>,
  ): Promise<FieldResponse | undefined> {
    try {
      const response = await this.post(`/processmodule/${organizationId}/${processModuleId}/field`, field);
      return response as FieldResponse;
    } catch (error) {
      this.onFailed(error);
      return undefined;
    }
  }

  async updateField(
    organizationId: string,
    fieldId: string,
    updates: Partial<FieldInput & Partial<Field>>,
  ): Promise<FieldResponse | undefined> {
    try {
      const response = await this.put(`/processmodule/${organizationId}/field/${fieldId}`, updates);
      return response as FieldResponse;
    } catch (error) {
      this.onFailed(error);
      return undefined;
    }
  }

  async deleteField(
    organizationId: string,
    fieldId: string,
    mode: 'unlink' | 'permanent' = 'permanent',
  ): Promise<{ ok: boolean }> {
    try {
      const response = await this.delete(`/processmodule/${organizationId}/field/${fieldId}?mode=${mode}`);
      return response as { ok: boolean };
    } catch (error) {
      this.onFailed(error);
      return { ok: false };
    }
  }

  // async createInfiniteCanvasForProcessModule({
  //   processModuleID,
  //   organizationId,
  //   canvasName,
  //   templateId,
  // }: {
  //   processModuleID: string;
  //   organizationId: string;
  //   canvasName: string;
  //   templateId?: string;
  // }): Promise<ApiResponse<ProcessModuleWithCanvas>> {
  //   // Generate a unique canvas ID
  //   const canvasId = crypto.randomUUID();

  //   // Create the infinite canvas
  //   await infiniteCanvasApi.getOrCreateInfiniteCanvas({
  //     teamId: organizationId,
  //     canvasId,
  //     config: { name: canvasName, templateId: templateId },
  //   });

  //   // Update the process module with the canvas ID
  //   return this.addCanvasToProcessModule(processModuleID, organizationId, {
  //     id: canvasId,
  //     // name: canvasName,
  //     // docId: canvasId,
  //     // createdAt: new Date().toISOString(),
  //     // updatedAt: new Date().toISOString(),
  //     // __typename: "ProcessModuleCanvas"
  //   });
  // }

  async addCanvasToProcessModule(
    processModuleID: string,
    organizationId: string,
    canvas: ProcessModuleCanvas,
  ): Promise<ApiResponse<ProcessModuleWithCanvas>> {
    return super.post(`/processmodule/${organizationId}/${processModuleID}/canvas`, { canvas });
  }

  async removeCanvasFromProcessModule(
    processModuleID: string,
    organizationId: string,
    canvasId: string,
  ): Promise<ApiResponse<ProcessModuleWithCanvas>> {
    return super.delete(`/processmodule/${organizationId}/${processModuleID}/canvas/${canvasId}`);
  }

  async getProcessModuleCanvases(
    processModuleID: string,
    organizationId: string,
  ): Promise<ApiResponse<ProcessModuleWithCanvas>> {
    return super.get(`/processmodule/${organizationId}/${processModuleID}/canvas`);
  }

  async getProcessModuleCanvasFields(
    processModuleID: string,
    organizationId: string,
  ): Promise<
    ApiResponse<{
      canvasFields: Array<{
        fieldId: string;
        fieldName: string;
        fieldLabel: string;
        canvasId: string;
        formId: string;
        formName: string;
      }>;
    }>
  > {
    return super.get(`/processmodule/${organizationId}/${processModuleID}/canvas-fields`);
  }

  async deleteProcessModule(organizationId: string, moduleId: string): Promise<ApiResponse<void>> {
    return super.delete(`/processmodule/${organizationId}/${moduleId}`);
  }

  async getProcessModuleStatus(moduleId: string): Promise<string | undefined> {
    const module = await this.getProcessModule(moduleId);
    return module?.status ?? undefined;
  }

  async updateProcessModuleStatus(
    organizationId: string,
    moduleId: string,
    status: string,
  ): Promise<ApiResponse<ProcessModule>> {
    return this.updateProcessModule({ status }, organizationId, moduleId);
  }

  async updateFormSubmissionStatus(teamId: string, formId: string, status: string, fieldId: string): Promise<any> {
    return this.patch(`/team/formsubmission/${teamId}/${formId}/status`, { status, fieldId });
  }

  async batchReorderForms(
    organizationId: string,
    processModuleID: string,
    reorderInput: { id: string; sequence: number }[],
  ): Promise<ApiResponse<{ updated: { id: string; sequence: number }[] }>> {
    return this.patch(`/processmodule/${organizationId}/${processModuleID}/forms/reorder`, reorderInput);
  }
}

export const processModuleApi = new ProcessModuleApi();
export default processModuleApi;
